<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    meter: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Meter', 'Edit General Information') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_m05k935sn_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_qukajwjbg_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_16iew6wd0_getActionArgs = async ($event) => {
    return {
      objectType: 'meter',
      op: 'upsert',
      data: props.meter,
    };
  };

  const act_7315092382398562_closeModal_m05k935sn =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_m05k935sn_getActionArgs,
    });
  const inst_evh_7315092382398562_close_1bxdda5t0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_m05k935sn,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_1bxdda5t0],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_neqe47318 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_dxy8iatud =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_neqe47318,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_dxy8iatud],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_6eobd5h37 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_fbgalu45y =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_6eobd5h37,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_fbgalu45y],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_qukajwjbg =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_qukajwjbg_getActionArgs,
    });
  const inst_evh_1040167445267876_success_scnv5okw2 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_qukajwjbg,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_crud_16iew6wd0 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_16iew6wd0_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_scnv5okw2],
  });
  const inst_evh_1040167445267876_submit_vmjayg945 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_16iew6wd0,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_vmjayg945],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_feovv9g7r =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_m8jaqy5xh =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_feovv9g7r,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_m8jaqy5xh],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_1bxdda5t0,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_dxy8iatud,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_fbgalu45y,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_scnv5okw2,
    inst_evh_1040167445267876_submit_vmjayg945,
    save: inst_evh_1040167445267876_submit_vmjayg945,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_m8jaqy5xh,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_1bxdda5t0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_1bxdda5t0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_dxy8iatud = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_dxy8iatud.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_fbgalu45y = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_fbgalu45y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_scnv5okw2 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_scnv5okw2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_vmjayg945 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_vmjayg945.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_m8jaqy5xh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_m8jaqy5xh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="meter"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetMeterGeneralData :meter="meter" :formId="formId">
          </FieldsetMeterGeneralData>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          type="submit"
          :form="forms.meter"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
